import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";

/**
 * PledgeDriveIdent — a "SUPPORT THE STATION" interstitial that plays
 * inside the CRT tube between channel changes. Styled like a 90s PBS
 * pledge drive / MTV donate spot: SMPTE colour bars sweep in, a
 * phosphor-green identity card holds with the operator's BMC link,
 * then everything fades back to the tape.
 *
 * Viewer interaction paths:
 *   • Do nothing → 4-stage timer auto-dismisses in ~4.2s.
 *   • Click RETURN TO SHOW → immediate dismiss.
 *   • Click BUY ME A COFFEE → immersive in-tube iframe view of the
 *     tip page + a "BACK TO SHOW" pill so returning is one click.
 *     We ALSO open BMC in a new tab as a fallback because most tip
 *     providers (BMC included) set X-Frame-Options / CSP frame-
 *     ancestors and block iframing outright — the iframe will still
 *     be attempted for the operator's benefit if their provider
 *     allows it, otherwise the new-tab always works.
 *   • When the user comes back to the CRT tab after paying, the
 *     visibility-change effect in CRT.js fires the golden THANK YOU
 *     bumper + unlocks the PATRON trophy (see the `crt_tip_pending_at`
 *     breadcrumb below).
 *
 * Auto-dismiss timers are cancelled the moment the viewer chooses
 * to interact — nobody should be nagged for a tip while they're
 * actually reading the pitch or browsing the tip page.
 */
const SMPTE_BARS = [
  "#c0c0c0", // white 75%
  "#c0c000", // yellow
  "#00c0c0", // cyan
  "#00c000", // green
  "#c000c0", // magenta
  "#c00000", // red
  "#0000c0", // blue
];

export function PledgeDriveIdent({ config, onDismiss }) {
  // Stages:
  //   bars    — 0-700ms SMPTE sweep
  //   card    — 700-3700ms holding the pitch card
  //   iframe  — viewer clicked CTA; tube embeds BMC + shows a back pill
  //   fade    — 3700-4200ms cross-fade to transparent, then unmount
  const [stage, setStage] = useState("bars");
  // Refs let interaction handlers cancel the auto-dismiss timers
  // without needing them in state (which would cause re-renders).
  const timersRef = useRef([]);

  useEffect(() => {
    const t1 = setTimeout(() => setStage("card"), 700);
    const t2 = setTimeout(() => setStage("fade"), 3700);
    const t3 = setTimeout(() => { onDismiss && onDismiss(); }, 4200);
    timersRef.current = [t1, t2, t3];
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [onDismiss]);

  const cancelTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const url = (config && config.url) || "";
  const headline = (config && config.headline) || "SUPPORT THE STATION";
  const subline = (config && config.subline) || "";
  const ctaLabel = (config && config.cta_label) || "BUY ME A COFFEE";

  const handleCta = (e) => {
    // Cancel the auto-dismiss so the viewer can browse the tip page
    // (either in-tube via the iframe or in the new tab we pop up)
    // for as long as they need.
    cancelTimers();
    // Breadcrumb — the visibilitychange effect in CRT.js reads this
    // to decide whether to fire the golden THANK YOU bumper + PATRON
    // trophy when the viewer returns to the tab.
    try { localStorage.setItem("crt_tip_pending_at", String(Date.now())); }
    catch (err) { /* noop */ }
    // Also open in a new tab as a fallback — BMC and most tip
    // providers set X-Frame-Options / CSP frame-ancestors to block
    // iframing, so the in-tube embed may show a blocked page. The
    // new tab guarantees the tip flow works either way.
    try { window.open(url, "_blank", "noopener,noreferrer"); }
    catch (err) { /* popup blocker — user can still use the in-tube view */ }
    setStage("iframe");
    // Prevent the anchor's default navigation of the parent frame —
    // we've already handled it with window.open above.
    if (e && e.preventDefault) e.preventDefault();
  };

  const handleBack = () => {
    cancelTimers();
    // Skip the fade and dismiss immediately — the viewer explicitly
    // asked for their tape back, no need to make them wait 500ms.
    onDismiss && onDismiss();
  };

  return (
    <div
      className="absolute inset-0 z-40"
      style={{
        // Fade the whole overlay in/out. The inner stages animate on
        // top of this envelope so viewers see a single smooth cross-
        // dissolve rather than a hard cut.
        opacity: stage === "fade" ? 0 : 1,
        transition: "opacity 500ms ease-out",
        // Pointer-events auto so the RETURN TO SHOW / BACK TO SHOW
        // buttons + the iframe are all clickable. The individual card
        // container still has its own background so viewers can't
        // click "through" to the tube behind.
        pointerEvents: stage === "fade" ? "none" : "auto",
      }}
      data-testid="pledge-drive-ident"
    >
      {/* SMPTE bar sweep — full-height horizontal stripes filling the
          tube. Only visible in the "bars" stage; cross-faded under the
          identity card afterwards. */}
      <div
        className="absolute inset-0 flex"
        style={{
          opacity: stage === "bars" ? 1 : 0,
          transition: "opacity 400ms ease-out",
        }}
        aria-hidden="true"
      >
        {SMPTE_BARS.map((c, i) => (
          <div key={i} style={{ flex: 1, backgroundColor: c }} />
        ))}
      </div>

      {/* Identity card — phosphor-green typography over a solid black
          plate. Only visible in the "card" stage. */}
      <div
        className="absolute inset-0 flex items-center justify-center bg-black text-center px-8"
        style={{
          opacity: stage === "card" ? 1 : 0,
          transition: "opacity 500ms ease-in",
          pointerEvents: stage === "card" ? "auto" : "none",
        }}
      >
        <div className="max-w-[92%] space-y-5">
          <div
            className="font-mono uppercase tracking-[0.35em] text-[color:var(--phosphor,#22ff88)]"
            style={{ fontSize: "clamp(1.1rem, 3.3vw, 2.1rem)", textShadow: "0 0 12px currentColor" }}
            data-testid="pledge-drive-headline"
          >
            {headline}
          </div>
          {subline && (
            <div
              className="font-mono uppercase tracking-[0.15em] text-[color:var(--phosphor,#22ff88)]/85 leading-relaxed"
              style={{ fontSize: "clamp(0.75rem, 1.4vw, 1rem)" }}
              data-testid="pledge-drive-subline"
            >
              {subline}
            </div>
          )}
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleCta}
              className="inline-block font-mono uppercase tracking-[0.3em] px-6 py-3 border-2 rounded-md"
              style={{
                borderColor: "var(--phosphor, #22ff88)",
                color: "var(--phosphor, #22ff88)",
                backgroundColor: "rgba(0,0,0,0.4)",
                fontSize: "clamp(0.8rem, 1.6vw, 1.05rem)",
                textShadow: "0 0 8px currentColor",
                boxShadow: "0 0 24px color-mix(in srgb, var(--phosphor, #22ff88) 45%, transparent)",
              }}
              data-testid="pledge-drive-cta"
            >
              ☕ {ctaLabel}
            </a>
          )}
          {/* Explicit escape hatch. Sits BELOW the CTA so it's the
              secondary action visually, but any viewer who doesn't
              want to tip can dismiss the ident instantly. */}
          <div>
            <button
              onClick={handleBack}
              className="font-mono uppercase tracking-[0.3em] text-[color:var(--phosphor,#22ff88)]/70 hover:text-[color:var(--phosphor,#22ff88)] underline decoration-dotted underline-offset-4"
              style={{ fontSize: "clamp(0.65rem, 1.15vw, 0.85rem)" }}
              data-testid="pledge-drive-back-card"
            >
              ◄ RETURN TO SHOW
            </button>
          </div>
        </div>
      </div>

      {/* Immersive tip screen — a phosphor QR code + human-readable
          BMC handle rendered natively inside the CRT tube. Chosen
          over an <iframe> because BMC (and every reputable tip
          provider) sets X-Frame-Options / CSP frame-ancestors to
          block embedding, so an iframe on production just paints
          BMC's "cannot be displayed" error page. A native QR screen:
          • never breaks (no cross-origin dependency),
          • matches the retro CRT aesthetic (phosphor-green dots
            over black — exactly what a scan-line CRT would render
            a QR code as),
          • lets a viewer tip from their PHONE while their laptop
            keeps the show playing — the most 2020s-feels-90s
            interaction you can offer,
          • still has the "GO TO TIP PAGE" pill for viewers who'd
            rather click through in the same browser. */}
      {stage === "iframe" && url && (
        <div
          className="absolute inset-0 bg-black flex flex-col items-center justify-center px-6"
          data-testid="pledge-drive-tip-screen"
        >
          <div
            className="font-mono uppercase tracking-[0.35em] text-[color:var(--phosphor,#22ff88)] mb-4"
            style={{ fontSize: "clamp(0.85rem, 1.9vw, 1.25rem)", textShadow: "0 0 10px currentColor" }}
          >
            SCAN TO TIP
          </div>
          {/* QR code — phosphor-green foreground on solid black so it
              reads through the scan-line overlay without inverting.
              Size clamps to the tube: ~35% of the shorter viewport
              axis via a fixed-ish 220px baseline, upgraded on
              larger screens by the container. */}
          <div
            className="p-3"
            style={{
              border: "2px solid var(--phosphor, #22ff88)",
              boxShadow: "0 0 24px color-mix(in srgb, var(--phosphor, #22ff88) 45%, transparent)",
              backgroundColor: "rgba(0,0,0,0.85)",
            }}
          >
            <QRCodeSVG
              value={url}
              size={220}
              fgColor="var(--phosphor, #22ff88)"
              bgColor="#000000"
              level="M"
              // Style the SVG so the phosphor gets a soft outer glow —
              // makes it feel like a real CRT rendering it rather than
              // a modern flat print.
              style={{ filter: "drop-shadow(0 0 4px var(--phosphor, #22ff88))" }}
              data-testid="pledge-drive-qr"
            />
          </div>
          {/* Human-readable handle so viewers who don't want to scan
              can still visually verify + type it in. Also acts as the
              "you're safe, this really is the operator" trust signal. */}
          <div
            className="mt-4 font-mono uppercase tracking-[0.22em] text-[color:var(--phosphor,#22ff88)]/85 text-center break-all max-w-[92%]"
            style={{ fontSize: "clamp(0.7rem, 1.15vw, 0.9rem)" }}
            data-testid="pledge-drive-url"
          >
            {url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
          </div>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              try { localStorage.setItem("crt_tip_pending_at", String(Date.now())); }
              catch (err) { /* noop */ }
            }}
            className="mt-5 font-mono uppercase tracking-[0.3em] px-5 py-2 border-2 rounded-md"
            style={{
              borderColor: "var(--phosphor, #22ff88)",
              color: "var(--phosphor, #22ff88)",
              backgroundColor: "rgba(0,0,0,0.6)",
              fontSize: "clamp(0.7rem, 1.3vw, 0.9rem)",
              textShadow: "0 0 6px currentColor",
              boxShadow: "0 0 16px color-mix(in srgb, var(--phosphor, #22ff88) 40%, transparent)",
            }}
            data-testid="pledge-drive-open-tab"
          >
            ↗ GO TO TIP PAGE
          </a>
          <button
            onClick={handleBack}
            className="mt-3 font-mono uppercase tracking-[0.3em] text-[color:var(--phosphor,#22ff88)]/70 hover:text-[color:var(--phosphor,#22ff88)] underline decoration-dotted underline-offset-4"
            style={{ fontSize: "clamp(0.65rem, 1.05vw, 0.8rem)" }}
            data-testid="pledge-drive-back-iframe"
          >
            ◄ BACK TO SHOW
          </button>
        </div>
      )}
    </div>
  );
}

export default PledgeDriveIdent;
